import { NgClass, NgTemplateOutlet } from '@angular/common'
import {
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  input,
  OnInit,
  Output,
  signal,
  TrackByFunction,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { NavigationSkipped, NavigationStart, Router, RouterModule } from '@angular/router'
import { Uuid } from '@ftr/contracts/type/shared'
import {
  BrowserSnifferService,
  ButtonComponent,
  FocusableNoopAnchorDirective,
  Icon,
  IconLegacyComponent,
  TooltipModule,
} from '@ftr/foundation'
import { featureFlag } from '@ftr/ui-feature-flags'
import { HELP_CENTER_NAV_TITLE, HelpMenuItemsInput, HelpModule } from '@ftr/ui-help'
import { AuthenticationService, GetCurrentUserGroupsAction } from '@ftr/ui-user'
import { NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngxs/store'
import { BranchNavItem, SideNavDisplayMode, SideNavGroup, SideNavItem } from '../../navigation/side-nav-item'
import { FavoritesEditModalComponent } from '../favorites/favorites-edit-modal/favorites-edit-modal.component'
import { FavoritesMenuComponent } from '../favorites/favorites-menu/favorites-menu.component'

@Component({
  selector: 'ftr-side-nav-shell',
  templateUrl: './side-nav-shell.component.html',
  styleUrls: ['./side-nav-shell.component.css'],
  imports: [
    IconLegacyComponent,
    ButtonComponent,
    NgClass,
    TooltipModule,
    NgTemplateOutlet,
    NgbPopoverModule,
    HelpModule,
    FavoritesMenuComponent,
    RouterModule,
    FavoritesEditModalComponent,
    FocusableNoopAnchorDirective,
  ],
})
export class SideNavShellComponent implements OnInit {
  private readonly router = inject(Router)
  private readonly store = inject(Store)
  private readonly browserSnifferService = inject(BrowserSnifferService)
  private readonly authenticationService = inject(AuthenticationService)

  @Input() displayMode: SideNavDisplayMode = 'DEFAULT'
  @Input() expanded: boolean = true
  @Input() isLoading: boolean = false
  @Input() currentCourtSystemId: Uuid | undefined
  @Input() isOpened: boolean
  @Output() sideBarToggled = new EventEmitter<void>()
  @Output() expandedToggled = new EventEmitter<void>()

  navGroups = input([] as SideNavGroup[])
  footerItems = input([] as SideNavItem[])
  helpMenuConfig = input.required<HelpMenuItemsInput>()
  elevioHelpSuffix = input<string | undefined>(undefined)
  theme = input<'light' | 'dark'>('dark')
  elevioHelpClass = computed(() => {
    const suffix = this.elevioHelpSuffix()
    return suffix ? `elevio__help-${suffix}` : 'elevio__help'
  })
  // TODO: This will be removed once we have proper light theme support
  elevioHelpIcon = input<Icon>('HelpCircleSolid')
  nestedNavItems = signal([] as BranchNavItem[])
  selectedNestedItem = computed(() =>
    this.nestedNavItems().length > 0 ? this.nestedNavItems()[this.nestedNavItems().length - 1] : undefined,
  )

  loadingWidths: string[] = Array.from({ length: 25 }, () => `${Math.floor(Math.random() * (96 - 55)) + 55}%`)
  loadingSideNavGroups: SideNavGroup[] = Array.from({ length: 4 }, () => ({
    isLoading: true,
  }))

  protected isExpandButtonHovered = signal(false)

  readonly helpCenterNavTitle = HELP_CENTER_NAV_TITLE

  readonly isFirefox = computed(() => this.browserSnifferService.isFirefox())

  protected readonly publicNavigationEnabled = featureFlag('public-navigation')

  readonly #destroyRef = inject(DestroyRef)

  async ngOnInit(): Promise<void> {
    if (await this.authenticationService.isLoggedIn) {
      this.store.dispatch(new GetCurrentUserGroupsAction())
    }
    this.watchRouterEvents()
  }

  openBranchNav(item: BranchNavItem): void {
    this.nestedNavItems.update(previousNavItems => [...previousNavItems, item])
  }

  popNestedNavItems(): void {
    this.nestedNavItems.update(previousNavItems => {
      return previousNavItems.slice(0, previousNavItems.length - 1)
    })
  }

  handleOnNavItemClick(sideNavItem: SideNavItem): void {
    if (sideNavItem.type === 'BRANCH') {
      this.openBranchNav(sideNavItem)
    } else if (sideNavItem.type === 'BUTTON') {
      if (sideNavItem.onSelect) {
        sideNavItem.onSelect()
      }
      this.closeSideBar()
    }
  }

  closeSideBar(): void {
    this.nestedNavItems.update(() => [])
    this.sideBarToggled.emit()
  }

  onFavoritesDropdownOpened(actions: SideNavItem[]): void {
    this.openBranchNav({
      type: 'BRANCH',
      title: 'Favorites',
      children: actions,
    })
  }

  trackBy: TrackByFunction<SideNavGroup | SideNavItem> = (index: number) => {
    return index
  }

  skipToMainContent(): void {
    const mainContent = document.getElementById('main-content')
    mainContent?.focus()
  }

  private watchRouterEvents(): void {
    this.router.events.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(event => {
      if (this.displayMode === 'OVERLAY' && this.isOpened) {
        if (event instanceof NavigationStart || event instanceof NavigationSkipped) {
          this.closeSideBar()
        }
      }
    })
  }
}
